import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SanitizationService } from './sanitization.service';

/**
 * HTTP Interceptor that automatically sanitizes all outgoing request bodies
 * and incoming response data to prevent XSS attacks
 * This provides a safety net to catch any data that wasn't sanitized at the service level
 */
@Injectable()
export class SanitizationInterceptor implements HttpInterceptor {
  constructor(private sanitizationService: SanitizationService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Sanitize request body for POST, PUT, PATCH requests
    if (
      req.body &&
      (req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH')
    ) {
      // Don't sanitize certain sensitive endpoints that need raw data
      const skipRequestSanitization = this.shouldSkipRequestSanitization(req.url);

      if (!skipRequestSanitization) {
        const sanitizedBody = this.sanitizeRequestBody(req.body);
        
        // Clone the request with sanitized body
        const sanitizedReq = req.clone({
          body: sanitizedBody,
        });

        return this.handleResponse(sanitizedReq, next);
      }
    }

    return this.handleResponse(req, next);
  }

  /**
   * Handle and sanitize response data
   */
  private handleResponse(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        // Only process successful HTTP responses
        if (event instanceof HttpResponse) {
          // Skip sanitization for binary responses (blobs, files, etc.)
          if (this.shouldSkipResponseSanitization(req.url, event)) {
            return event;
          }

          // Sanitize the response body
          const sanitizedBody = this.sanitizeResponseBody(event.body);
          
          // Return cloned response with sanitized body
          return event.clone({ body: sanitizedBody });
        }
        return event;
      })
    );
  }

  /**
   * Determine if request sanitization should be skipped for specific URLs
   */
  private shouldSkipRequestSanitization(url: string): boolean {
    // Use regex patterns for precise matching (start of path or exact match)
    const skipPatterns = [
      /^\/api\/account\/login(\/|$)/,        // Password should not be sanitized
      /^\/api\/account\/resetPassword(\/|$)/, // Password fields
      /^\/upload(\/|$)/,                      // File uploads
      /^\/import(\/|$)/,                      // Data imports
    ];

    return skipPatterns.some(pattern => pattern.test(url));
  }

  /**
   * Determine if response sanitization should be skipped
   */
  private shouldSkipResponseSanitization(url: string, response: HttpResponse<any>): boolean {
    // Skip sanitization for binary/blob responses
    if (response.body instanceof Blob || response.body instanceof ArrayBuffer) {
      return true;
    }

    // Skip for file download endpoints (precise matching)
    const skipPatterns = [
      /^\/download(\/|$)/,
      /^\/file(\/|$)/,
      /^\/export(\/|$)/,
      /^\/pdf(\/|$)/,
      /^\/blob(\/|$)/
    ];

    return skipPatterns.some(pattern => pattern.test(url));
  }

  /**
   * Check if a field name suggests it's a date field
   */
  private isDateField(fieldName: string): boolean {
    const dateFieldPatterns = [
      /date/i,
      /time/i,
      /timestamp/i,
      /created/i,
      /updated/i,
      /modified/i,
      /expires/i,
      /expiry/i,
      /due/i,
      /start/i,
      /end/i,
      /birth/i,
      /dob/i
    ];
    
    return dateFieldPatterns.some(pattern => pattern.test(fieldName));
  }

  /**
   * Recursively sanitize the request body
   */
  private sanitizeRequestBody(body: any): any {
    if (!body) return body;

    // Handle FormData separately (file uploads)
    if (body instanceof FormData) {
      return body;
    }

    // Handle Date objects
    if (body instanceof Date) {
      return body;
    }

    // Handle arrays
    if (Array.isArray(body)) {
      return body.map(item => this.sanitizeRequestBody(item));
    }

    // Handle objects
    if (typeof body === 'object' && body !== null) {
      // Don't sanitize password fields or date fields
      const passwordFields = [
        'password', 'newPassword', 'currentPassword', 'confirmPassword',
        'repeatPassword', 'oldPassword', 'verifyPassword', 'passwd', 'pwd'
      ];
      
      const sanitized: any = {};
      for (const key in body) {
        if (body.hasOwnProperty(key)) {
          // Skip password fields (case-insensitive, substring match) and date fields
          const keyLower = key.toLowerCase();
          const isPasswordField = passwordFields.some(field => keyLower.includes(field.toLowerCase())) || keyLower.includes('password');
          if (isPasswordField || this.isDateField(key)) {
            sanitized[key] = body[key];
          } else if (typeof body[key] === 'string') {
            sanitized[key] = this.sanitizationService.sanitizeText(body[key]);
          } else if (typeof body[key] === 'object' || Array.isArray(body[key])) {
            sanitized[key] = this.sanitizeRequestBody(body[key]);
          } else {
            sanitized[key] = body[key];
          }
        }
      }
      return sanitized;
    }

    // Handle strings
    if (typeof body === 'string') {
      return this.sanitizationService.sanitizeText(body);
    }

    return body;
  }

  /**
   * Recursively sanitize response body from API
   * This sanitizes all string values in the response to prevent XSS
   */
  private sanitizeResponseBody(body: any): any {
    if (!body) return body;

    // Handle primitive types
    if (typeof body === 'string') {
      return this.sanitizationService.sanitizeText(body);
    }

    if (typeof body === 'number' || typeof body === 'boolean') {
      return body;
    }

    // Handle Date objects
    if (body instanceof Date) {
      return body;
    }

    // Handle arrays
    if (Array.isArray(body)) {
      return body.map(item => this.sanitizeResponseBody(item));
    }

    // Handle objects
    if (typeof body === 'object' && body !== null) {
      const sanitized: any = {};
      
      for (const key in body) {
        if (body.hasOwnProperty(key)) {
          const value = body[key];
          
          // Skip sanitization for date fields or date values
          if (this.isDateField(key) || this.isDateValue(value)) {
            sanitized[key] = value;
            continue;
          }

          // Sanitize strings
          if (typeof value === 'string') {
            sanitized[key] = this.sanitizationService.sanitizeText(value);
          } 
          // Recursively sanitize nested objects and arrays
          else if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
            sanitized[key] = this.sanitizeResponseBody(value);
          } 
          // Keep other types as-is
          else {
            sanitized[key] = value;
          }
        }
      }
      
      return sanitized;
    }

    return body;
  }

  /**
   * Check if a value appears to be a date
   */
  private isDateValue(value: any): boolean {
    if (!value) return false;
    
    // Check if it's already a Date object
    if (value instanceof Date) return true;
    
    // Check if it's a string that looks like a date
    if (typeof value === 'string') {
      // ISO 8601 date format or common date patterns
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}/, // MM/DD/YYYY or DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}/, // DD-MM-YYYY or MM-DD-YYYY
        /^\d{4}\/\d{2}\/\d{2}/, // YYYY/MM/DD
      ];
      
      if (datePatterns.some(pattern => pattern.test(value.trim()))) {
        return true;
      }
      
      // Try parsing as date - but be conservative
      const parsed = Date.parse(value);
      if (!isNaN(parsed) && value.length > 8) {
        return true;
      }
    }
    
    return false;
  }
}
